import type { ClassValue } from 'clsx'

import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

import { COLLECTION_TASK_CONSTANTS } from '../constants/collection-task'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * 格式化文件大小
 * @param bytes 字节数
 * @returns 格式化后的文件大小字符串
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0)
    return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${Number.parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`
}

/**
 * 格式化时长（秒数转换为 mm:ss 格式）
 * @param seconds 秒数
 * @returns 格式化后的时长字符串
 */
export function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

/**
 * 格式化日期字符串
 * @param dateString 日期字符串
 * @returns 格式化后的日期字符串
 */
export function formatDate(dateString?: string): string {
  if (!dateString)
    return '-'
  return new Date(dateString).toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * 格式化日期字符串（包含秒）
 * @param dateString 日期字符串
 * @returns 格式化后的日期字符串
 */
export function formatDateTime(dateString?: string): string {
  if (!dateString)
    return '-'
  return new Date(dateString).toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

/**
 * 格式化时间戳（支持 protobuf Timestamp 和 ISO 字符串）
 * @param timestamp 时间戳对象或字符串
 * @returns 格式化后的日期字符串
 */
export function formatTimestamp(timestamp?: { seconds: string | number, nanos?: number } | string): string {
  if (!timestamp)
    return '-'

  // 如果是字符串格式（ISO 8601）
  if (typeof timestamp === 'string') {
    const date = new Date(timestamp)
    return Number.isNaN(date.getTime()) ? '-' : formatDate(timestamp)
  }

  // 如果是 protobuf Timestamp 格式
  if (typeof timestamp === 'object' && timestamp.seconds) {
    const seconds = typeof timestamp.seconds === 'string'
      ? Number.parseInt(timestamp.seconds)
      : timestamp.seconds
    const date = new Date(seconds * 1000)
    return Number.isNaN(date.getTime())
      ? '-'
      : date.toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        })
  }

  return '-'
}

/**
 * 获取时间戳数值用于排序比较
 * @param timestamp 时间戳对象或字符串
 * @returns 时间戳毫秒数
 */
export function getTimestamp(timestamp?: { seconds: string | number, nanos?: number } | string): number {
  if (!timestamp)
    return 0

  // 如果是字符串格式（ISO 8601）
  if (typeof timestamp === 'string') {
    const date = new Date(timestamp)
    return Number.isNaN(date.getTime()) ? 0 : date.getTime()
  }

  // 如果是 protobuf Timestamp 格式
  if (typeof timestamp === 'object' && timestamp.seconds) {
    const seconds = typeof timestamp.seconds === 'string'
      ? Number.parseInt(timestamp.seconds)
      : timestamp.seconds
    return seconds * 1000 // 转换为毫秒
  }

  return 0
}

/**
 * 格式化速度（字节/秒）
 * @param bytesPerSecond 每秒字节数
 * @returns 格式化后的速度字符串
 */
export function formatSpeed(bytesPerSecond: number): string {
  return `${formatFileSize(bytesPerSecond)}/s`
}

/**
 * 格式化剩余时间
 * @param seconds 剩余秒数
 * @returns 格式化后的时间字符串
 */
export function formatRemainingTime(seconds: number): string {
  if (seconds === Infinity || Number.isNaN(seconds))
    return '未知'
  if (seconds < 60)
    return `${Math.round(seconds)}秒`
  if (seconds < 3600)
    return `${Math.round(seconds / 60)}分钟`
  return `${Math.round(seconds / 3600)}小时`
}

/**
 * 格式化JSON数据
 * @param data 要格式化的数据对象
 * @returns 格式化后的JSON字符串
 */
export function formatJsonData(data?: Record<string, unknown>): string {
  if (!data)
    return '无数据'
  return JSON.stringify(data, null, 2)
}

/**
 * 格式化日期为 HTML date input 格式 (YYYY-MM-DD)
 * @param dateString 日期字符串
 * @returns 格式化后的日期字符串
 */
export function formatDateForInput(dateString?: string): string {
  if (!dateString)
    return ''
  const date = new Date(dateString)
  if (Number.isNaN(date.getTime()))
    return ''
  return date.toISOString().split('T')[0]
}

/**
 * 获取视频缩略图 URL（通过拼接 CI 处理参数）
 * @param videoUrl 原始视频 URL
 * @returns 带有截图参数的缩略图 URL（保持原始比例）
 */
/**
 * 移除文件扩展名
 * 使用负向先行断言确保不会错误处理以点开头的文件名（如 .bashrc）
 * @param filename 文件名
 * @returns 移除扩展名后的文件名
 */
export function removeFileExtension(filename: string): string {
  return filename.replace(/(?<!^)\.[^/.]+$/, '')
}

export function getVideoThumbnail(videoUrl: string): string {
  if (!videoUrl)
    return ''
  // 检查 URL 是否已有查询参数
  const separator = videoUrl.includes('?') ? '&' : '?'
  // 不设置 width 和 height，保持视频原始比例
  return `${videoUrl}${separator}ci-process=snapshot&time=0&format=jpg&mode=keyframe`
}

/**
 * 计算字符串的字符数（正确处理emoji等Unicode字符）
 * @param str 要计算的字符串
 * @returns 字符数
 */
export function getCharCount(str: string): number {
  if (!str)
    return 0
  return Array.from(str).length
}

/**
 * 验证字符串是否超过指定的字符数
 * @param str 要验证的字符串
 * @param maxCount 最大字符数
 * @returns 是否超过限制
 */
export function exceedsCharLimit(str: string, maxCount: number): boolean {
  return getCharCount(str) > maxCount
}

/**
 * 根据用户名模糊搜索用户
 * @param users 用户列表
 * @param searchTerm 搜索关键词
 * @returns 匹配的用户列表
 */
export function filterUsersByName(
  users: Array<{ id: string, username: string }>,
  searchTerm: string,
): Array<{ id: string, username: string }> {
  if (!searchTerm || !searchTerm.trim())
    return []

  const term = searchTerm.trim().toLowerCase()
  return users.filter(user =>
    user.username.toLowerCase().includes(term),
  )
}

/**
 * 检查字符串是否为UUID格式
 * @param str 要检查的字符串
 * @returns 是否为UUID格式
 */
export function isUUID(str: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
  return uuidRegex.test(str)
}

/**
 * 格式化UUID显示（显示前8位和后4位，中间用省略号）
 * @param uuid UUID字符串
 * @returns 格式化后的UUID字符串，例如 "12345678-...-abcd"
 */
export function formatUUID(uuid?: string | null): string {
  if (!uuid)
    return '-'
  if (!isUUID(uuid))
    return uuid
  // UUID格式: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
  // 显示: 前8位 + ... + 后4位
  const parts = uuid.split('-')
  if (parts.length === 5) {
    return `${parts[0]}-...-${parts[4]}`
  }
  return uuid
}

/**
 * 构建征集任务分享链接
 * @param shareToken 分享token
 * @param recordWebUrl 拍摄服务URL
 * @param regionKey 区域key
 * @returns 完整的分享链接
 */
export function buildCollectionTaskShareUrl(
  shareToken: string,
  recordWebUrl: string,
  regionKey: string,
): string {
  try {
    const url = new URL(recordWebUrl)
    url.searchParams.set(COLLECTION_TASK_CONSTANTS.SHARE_TOKEN_PARAM, shareToken)
    url.searchParams.set(COLLECTION_TASK_CONSTANTS.REGION_PARAM, regionKey)
    return url.toString()
  }
  catch (error) {
    console.warn('Failed to parse recordWebUrl, using string concatenation:', error)
    // 处理 recordWebUrl 已包含查询参数的情况
    const separator = recordWebUrl.includes('?') ? '&' : '?'
    return `${recordWebUrl}${separator}${COLLECTION_TASK_CONSTANTS.SHARE_TOKEN_PARAM}=${shareToken}&${COLLECTION_TASK_CONSTANTS.REGION_PARAM}=${regionKey}`
  }
}
